import {Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent} from "@components";
import {EventItem, FetchRaceByIdRequest, Lookups, Race, SportType} from "@model";
import {LoaderService, RaceService, TOAST_STATE, ToastService} from "@services";
import {required} from "@utility";
import {RaceDetailsComponentType} from "../admin-race-details.component";
import {take} from "rxjs";

@Component({
  selector: 'app-race-details-basic-info',
  templateUrl: './race-details-basic-info.component.html',
  styleUrls: ['./race-details-basic-info.component.scss']
})
export class RaceDetailsBasicInfoComponent extends AbstractComponent implements OnInit {

  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() disableEditing: boolean = false;
  @Input() sports: Lookups[] = [];
  @Input() statuses: Lookups[] = [];
  @Input() registrationTypes: Lookups[] = [];
  @Input() componentType: RaceDetailsComponentType = RaceDetailsComponentType.EDIT;
  @Output() raceBasicDataChange: EventEmitter<Race | undefined> = new EventEmitter<Race | undefined>();

  editForm: boolean = false;
  isAdmin: boolean = false;

  applicationMaxDate: Date | undefined;
  currentDate = new Date()
  calendarStartDate?: Date;

  inputForm = new FormGroup({
    organizerName: new FormControl(),
    name: new FormControl(),
    nameEng: new FormControl(),
    nameIta: new FormControl(),
    nameSlo: new FormControl(),
    leagueName: new FormControl(),
    leagueNameEn: new FormControl(),
    leagueNameSl: new FormControl(),
    leagueNameIt: new FormControl(),
    round: new FormControl(),
    status: new FormControl(),
    registrationType: new FormControl(),
    sport: new FormControl(),
    virtualRace: new FormControl(),
    raceDates: new FormControl(),
    registrationDates: new FormControl(),
    startEvery: new FormControl(),
    maxParticipants: new FormControl(),
    length: new FormControl(),
    ascent: new FormControl(),
    descent: new FormControl(),
    maxAltitude: new FormControl(),
    minAltitude: new FormControl(),
    checkpointAmount: new FormControl(),
    refreshmentCheckpointAmount: new FormControl(),
  });
  selectedImage: string | undefined;

  constructor(
    public translocoService: TranslocoService,
    private raceService: RaceService,
    private toastService: ToastService,
    private loaderService: LoaderService,
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToFormValueChanges();
    this.subscribeToLeagueNameChange();
    this.subscribeToCurrentRaceChange();
    this.raceItemChanged();
    this.initForm();
    this.calendarStartDate = new Date(this.currentDate.setDate(this.currentDate.getDate() + 1))
    this.calendarStartDate.setHours(0, 0, 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['race']?.currentValue !== changes['race']?.previousValue) {
      this.raceItemChanged()
    }
  }

  private subscribeToFormValueChanges(): void {
    this.subscriptions.push(this.inputForm.controls.sport.valueChanges.subscribe((selectedSport: number) => {
      switch (selectedSport) {
        case SportType.ROAD_RUNNING:
        case SportType.CROSS_RUNNING:
        case SportType.CYCLING_CYCLOCROSS:
        case SportType.CYCLING_ENDURO:
        case SportType.CYCLING_GRAVEL:
        case SportType.CYCLING_MOUNTAINBIKE:
        case SportType.CYCLING_PUMPTRACK:
        case SportType.CYCLING_ROAD:
          this.enableControls(this.racingSportRequiredFields)
          break;
        case SportType.SWIMMING:
          this.enableControls(this.swimmingSportDependantFields)
          this.disableControls(this.swimmingSportNonRequiredFields)
          break;
        default:
          this.disableControls(this.racingSportRequiredFields);
          break;
      }
      this.inputForm.updateValueAndValidity();
    }))
    this.subscriptions.push(this.inputForm.controls.raceDates.valueChanges.subscribe(_ => {
      this.applicationMaxDate = this.getApplicationMaxDate();
    }))
  }

  get racingSportRequiredFields(): (keyof Race)[] {
    return ["length", "ascent", "descent", "maxAltitude", "minAltitude"];
  }

  get swimmingSportDependantFields(): (keyof Race)[] {
    return ["length"];
  }

  get swimmingSportNonRequiredFields(): (keyof Race)[] {
    return ["ascent", "descent", "maxAltitude", "minAltitude"];
  }

  private enableControls(fields: string[]) {
    for (let field of fields) {
      const control = this.inputForm.get(field)
      if (control) {
        control.enable();
        control.setValue(null)
        control.setValidators([required]);
      }
    }
  }

  private disableControls(fields: string[]) {
    for (let field of fields) {
      const control = this.inputForm.get(field)
      if (control) {
        control.removeValidators([required]);
        control.setValue(null)
        control.disable();
      }
    }
  }

  private subscribeToCurrentRaceChange() {
    this.raceService.currentRace.subscribe((race: Race | undefined) => {
      this.race = race;
      this.raceItemChanged()
    })
  }

  private subscribeToLeagueNameChange() {
    this.subscriptions.push(
      this.inputForm.controls.leagueName.valueChanges.subscribe(_ => {
        this.leagueNameChanged();
      }));
    this.subscriptions.push(
      this.inputForm.controls.leagueNameEn.valueChanges.subscribe(_ => {
        this.leagueNameChanged();
      }));
    this.subscriptions.push(
      this.inputForm.controls.leagueNameSl.valueChanges.subscribe(_ => {
        this.leagueNameChanged();
      }));
    this.subscriptions.push(
      this.inputForm.controls.leagueNameIt.valueChanges.subscribe(_ => {
        this.leagueNameChanged();
      }));
  }

  private leagueNameChanged() {
    if (this.isLeagueNameFilled()) {
      this.inputForm.controls.round.setValidators([required]);
    } else {
      this.inputForm.controls.round.removeValidators([required]);
    }
    this.inputForm.controls.round.updateValueAndValidity();
  }

  private isLeagueNameFilled() {
    return !!(this.inputForm.controls.leagueName.value ||
      this.inputForm.controls.leagueNameEn.value ||
      this.inputForm.controls.leagueNameIt.value ||
      this.inputForm.controls.leagueNameSl.value);
  }

  private initForm() {
    this.inputForm.controls.status.addValidators([required]);
    this.inputForm.controls.registrationType.addValidators([required]);
    this.inputForm.controls.sport.addValidators([required]);
    this.inputForm.controls.virtualRace.addValidators([required]);
    this.inputForm.controls.raceDates.addValidators([required]);
    this.inputForm.controls.registrationDates.addValidators([required]);
    this.inputForm.controls.maxParticipants.addValidators([required]);
    this.inputForm.controls.startEvery.addValidators([required]);
    if (this.disableEditing) {
      this.inputForm.disable();
    }
  }

  private populateEventDependantFields() {
    this.inputForm.controls.organizerName.setValue(this.event?.organiser?.name);
    this.inputForm.controls.organizerName.disable();

    if (this.event?.leagueName && !this.race?.leagueName) {
      this.inputForm.controls.leagueName.setValue(this.event?.leagueName);
    }

    if (this.event?.leagueNameEn && !this.race?.leagueNameEn) {
      this.inputForm.controls.leagueNameEn.setValue(this.event?.leagueNameEn);
    }

    if (this.event?.leagueNameIt && !this.race?.leagueNameIt) {
      this.inputForm.controls.leagueNameIt.setValue(this.event?.leagueNameIt);
    }

    if (this.event?.leagueNameSl && !this.race?.leagueNameSl) {
      this.inputForm.controls.leagueNameSl.setValue(this.event?.leagueNameSl);
    }
    //todo what about league id ???

    if (this.event?.round && !this.race?.round) {
      this.inputForm.controls.round.setValue(this.event.round);
    }
    if (this.event?.eventType && this.event.eventType !== this.race?.registrationType) {
      this.inputForm.controls.registrationType.setValue((this.event.eventType));
    }
    this.inputForm.controls.registrationType.disable();


    if (this.event?.sportOne && !this.race?.sport && (this.event.sportOne !== SportType.MULTISPORT && this.event.sportTwo !== SportType.MULTISPORT)) {
      this.inputForm.controls.sport.setValue(this.event.sportOne);
    }
  }

  private raceItemChanged(): void {
    if (this.race) {
      this.inputForm.patchValue(this.race);
      this.inputForm.controls.raceDates.patchValue({from: this.race.raceStart, to: this.race.raceEnd});
      this.inputForm.controls.registrationDates.patchValue({
        from: this.race.registrationStart,
        to: this.race.registrationEnd
      });
      this.selectedImage = this.race.imageMedium;
      if (this.race.id) {
        this.editForm = true;
      }
      if (!this.race.id) {
        this.inputForm.controls.virtualRace.setValue(false);
      }
    }
    if (this.event) {
      this.populateEventDependantFields();
    }
  }


  private getRaceFields(): Race {
    this.checkValidators(this.inputForm);
    let raceDetails: Race = {...this.race};

    raceDetails.organiser = this.event?.organiser;
    raceDetails.organiserId = this.event?.organiserId;
    raceDetails.eventId = this.event?.id;

    raceDetails.name = this.inputForm.value.name;
    raceDetails.nameEng = this.inputForm.value.nameEng;
    raceDetails.nameIta = this.inputForm.value.nameIta;
    raceDetails.nameSlo = this.inputForm.value.nameSlo;
    raceDetails.leagueName = this.inputForm.value.leagueName;
    raceDetails.leagueNameEn = this.inputForm.value.leagueNameEn;
    raceDetails.leagueNameIt = this.inputForm.value.leagueNameIt;
    raceDetails.leagueNameSl = this.inputForm.value.leagueNameSl;

    raceDetails.status = this.inputForm.value.status;
    raceDetails.registrationStart = this.inputForm.value.registrationDates?.from;
    raceDetails.registrationEnd = this.inputForm.value.registrationDates?.to;
    raceDetails.raceStart = this.inputForm.value.raceDates?.from;
    raceDetails.raceEnd = this.inputForm.value.raceDates?.to;
    raceDetails.maxParticipants = this.inputForm.value.maxParticipants;
    raceDetails.startEvery = this.inputForm.value.startEvery;

    raceDetails.round = this.inputForm.value.round;
    raceDetails.registrationType = this.inputForm.controls.registrationType.value;
    raceDetails.sport = this.inputForm.value.sport;
    raceDetails.virtualRace = this.inputForm.value.virtualRace;

    //todo - check with null values in DB
    raceDetails.length = this.inputForm.value.length ?? 0;
    raceDetails.ascent = this.inputForm.value.ascent ?? 0;
    raceDetails.descent = this.inputForm.value.descent ?? 0;
    raceDetails.maxAltitude = this.inputForm.value.maxAltitude ?? 0;
    raceDetails.minAltitude = this.inputForm.value.minAltitude ?? 0;

    raceDetails.checkpointAmount = this.inputForm.value.checkpointAmount;
    raceDetails.refreshmentCheckpointAmount = this.inputForm.value.refreshmentCheckpointAmount;
    raceDetails.imageMedium = this.selectedImage;
    return raceDetails;
  }

  imageChange(image: string | undefined) {
    this.selectedImage = image;
  }

  getApplicationMaxDate() {
    if (this.inputForm.controls.raceDates.value["from"]) {
      const newDate = new Date(this.inputForm.controls.raceDates.value["from"]);
      newDate.setDate(newDate.getDate() - 1); // Subtract one day
      return newDate;
    }
    return undefined;
  }

  onSuccess(raceId: number, key: string): void {
    this.raceService.findRaceByIdProtected(new FetchRaceByIdRequest(raceId), true)
      .pipe(take(1)).subscribe((race: Race | null) => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.success,
        this.translocoService.translate(key))
      this.raceBasicDataChange.emit(race ?? undefined);
    })
  }

  onError(key: string) {
    this.toastService.showToast(TOAST_STATE.danger,
      this.translocoService.translate(key))
  }

  copyRace(raceDetails: Race): void {
    this.loaderService.startLoading();
    this.raceService.copyRace(raceDetails, (raceId: number) => {
      this.onSuccess(raceId, "app.common.success.race.copy")
    }, () => {
      this.onError("app.common.server.error")
    });
  }

  updateRace(raceDetails: Race): void {
    this.loaderService.startLoading();
    this.raceService.updateRace(raceDetails, (raceId: number) => {
      this.onSuccess(raceId, "app.common.success.race.edit")
    }, () => {
      this.onError("app.common.server.error")
    });
  }

  onSave(): void {
    if (!this.inputForm.valid || this.disableEditing) {
      this.markAllAsTouched(this.inputForm);
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("event.item.error.form"))
      return;
    } else {
      let raceDetails: Race = this.getRaceFields();
      switch (this.componentType) {
        case RaceDetailsComponentType.RACE_COPY:
          this.copyRace(raceDetails);
          break;
        case RaceDetailsComponentType.ADD:
        case RaceDetailsComponentType.EDIT:
          this.updateRace(raceDetails)
          break;
        default:
          break;
      }
    }
  }
}
